/**
 * Utility for resolving column labels from schema data.
 */

import type { AssociationType } from '../../core/SchemaClient';
import type { ColumnConfig } from '../../core/types';
import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '../../hooks/usePropertySchema';
import type { EntityTypeSchemaLike } from '../../utils/associationMatching';
import {
  buildLookupColumnName,
  getLookupColumnParts,
  resolveLookupTargetType,
} from './lookupColumn';

/**
 * Get the display label for a column based on its kind and schema data.
 *
 * - 'object_name': Returns the entity type label (e.g., "Contact", "Company")
 * - 'property': Returns the property type label from schema
 * - 'association': Returns the association type label from schema
 * - 'lookup': Returns the column label override, or a fallback derived from the target property name
 *
 * @param column - Column configuration
 * @param entityTypeLabel - Label for the entity type (e.g., "Contact")
 * @param propertySchemas - Map of property type names to schemas
 * @param associationTypes - Array of association type schemas
 * @param lookupTargetSchemasByType - Map of entity type name to property schema map, used to resolve the real label of a lookup column's target property
 * @param objectTypes - Entity type schemas, used to resolve the concrete target
 *   type of a kind-based lookup association before indexing
 *   `lookupTargetSchemasByType` (so kind-based lookup headers use the schema
 *   label, matching the picker, instead of a synthesized property name)
 * @returns Display label for the column header
 */
export function getColumnLabel(
  column: ColumnConfig,
  entityTypeLabel: string,
  propertySchemas: PropertySchemaMap,
  associationTypes: AssociationType[],
  lookupTargetSchemasByType?: Record<
    string,
    Record<string, PropertyTypeSchema>
  >,
  objectTypes?: EntityTypeSchemaLike[]
): string {
  let label: string;

  switch (column.kind) {
    case 'object_name':
      label = entityTypeLabel;
      break;

    case 'property': {
      if (!column.name) {
        return 'Property';
      }
      const schema = propertySchemas[column.name];
      label = column.label || schema?.label || formatPropertyName(column.name);
      break;
    }

    case 'association': {
      if (!column.name) {
        return 'Association';
      }
      const assocType = associationTypes.find(
        a => a.association_type_name === column.name
      );
      label =
        column.label || assocType?.label || formatAssociationName(column.name);
      break;
    }

    case 'lookup': {
      const parts = getLookupColumnParts(column);
      if (!parts) {
        label = column.label || 'Lookup';
        break;
      }
      const assocType = associationTypes.find(
        a => a.association_type_name === parts.associationTypeName
      );
      const assocLabel =
        assocType?.label || formatAssociationName(parts.associationTypeName);
      // Resolve the concrete target type — type-based associations expose it
      // directly, kind-based associations resolve to a concrete entity type via
      // objectTypes (excluding `*_timeline` aliases) — so kind-based lookup
      // headers index the target schema and use its label (matching the picker)
      // rather than falling back to a synthesized property name.
      const targetType = resolveLookupTargetType(assocType, objectTypes);
      const targetSchema = targetType
        ? lookupTargetSchemasByType?.[targetType]
        : undefined;
      const targetLabel =
        targetSchema?.[parts.targetPropertyName]?.label ||
        formatPropertyName(parts.targetPropertyName);
      label = column.label || `${assocLabel} · ${targetLabel}`;
      break;
    }

    case 'email_initiator':
      label = 'Initiated by';
      break;

    case 'type':
      label = column.label || 'Type';
      break;
  }

  return label;
}

/**
 * Capitalize the first letter of every word. Schema labels in this codebase
 * are written in title case (e.g. "First Name", "Phone Number"), so the
 * fallback matches that convention to avoid a sentence-case → title-case
 * flash when schemas finish loading.
 */
function toTitleCase(words: string[]): string {
  return words
    .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/**
 * Format a property name as a fallback label.
 * Converts 'ahoy_first_name' to 'First Name' (title case to match schema labels).
 */
function formatPropertyName(name: string): string {
  return toTitleCase(name.replace(/^ahoy_/, '').split('_'));
}

/**
 * Format an association type name as a fallback label.
 * Converts 'ahoy_contact_to_company' to 'Company'.
 * Converts 'ahoy_contact_to_lifecycle_stage' to 'Lifecycle Stage'.
 * A name that doesn't match the 'ahoy_X_to_Y' pattern (e.g. custom
 * 'org_X_to_Y' association types) is returned unchanged.
 */
export function formatAssociationName(name: string): string {
  // Extract target from 'ahoy_X_to_Y' pattern (Y may contain underscores)
  const match = name.match(/ahoy_\w+?_to_(.+)/);
  if (match) {
    return toTitleCase(match[1].split('_'));
  }
  return name;
}

/**
 * Get a unique key for a column (used for React keys and sorting).
 * Uses the column name, falling back to kind for object_name columns.
 */
export function getColumnKey(column: ColumnConfig): string {
  // Lookup columns share the `kind: 'lookup'` discriminator, so falling back to
  // `column.kind` would collapse every lookup column onto the same key — table
  // reconciliation and saved column ordering would then collide when more than
  // one lookup is configured. Derive the stable compound key
  // (`<associationTypeName>.<targetPropertyName>`) from the column's parts so
  // each lookup column keys uniquely even if its `name` was left unset.
  if (column.kind === 'lookup') {
    const parts = getLookupColumnParts(column);
    if (parts) {
      return buildLookupColumnName(
        parts.associationTypeName,
        parts.targetPropertyName
      );
    }
  }
  return column.name || column.kind;
}
